/**
 * Cloudflare Turnstile integratie.
 *
 * Lazy-loadt het Cloudflare-script in document.head en wraps de globale
 * window.turnstile API in async-vriendelijke helpers.
 *
 * Werking:
 *   1. ensureTurnstileScript() injecteert <script src="https://challenges...">
 *      in document.head (idempotent — meerdere calls zijn veilig).
 *   2. Wacht tot window.turnstile beschikbaar is.
 *   3. renderTurnstileWidget(container, options) → widget-id (string).
 *   4. Bij token: callback fires met de string die we naar de server sturen
 *      als captchaToken in POST /conversations/requests/anonymous/new.
 *
 * Shadow DOM: container kan een DOM-node binnen een Shadow Root zijn.
 * Turnstile rendert intern een iframe — werkt correct vanaf moderne browsers.
 *
 * CSP: host-site moet `script-src https://challenges.cloudflare.com` toestaan.
 * Documenteren in integratie-handleiding.
 */

const SCRIPT_ID = 'embrace-chat-turnstile';
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

/**
 * Attribuut waarmee Turnstile-portals worden gemarkeerd. Door zowel de
 * portal-elementen als de globale CSS-regel deze constante te laten
 * delen kunnen ze niet uit elkaar lopen.
 */
export const TURNSTILE_PORTAL_ATTR = 'data-turnstile-portal';

/**
 * Cloudflare's flexible-mode laat de iframe na een display:none/block-
 * cyclus soms op z'n minimum (300px) hangen. Een !important-regel wint
 * van Cloudflare's eigen inline-style en houdt de iframe in lijn met de
 * portal-breedte zonder dat we per-instance JS-coercion hoeven te doen.
 */
let portalStyleInstalled = false;
export function ensureTurnstilePortalStyle(): void {
  if (portalStyleInstalled) return;
  portalStyleInstalled = true;
  const style = document.createElement('style');
  style.textContent = `[${TURNSTILE_PORTAL_ATTR}] iframe { width: 100% !important; }`;
  document.head.appendChild(style);
}

export type TurnstileWidgetId = string;

export type TurnstileTheme = 'light' | 'dark' | 'auto';
export type TurnstileSize = 'normal' | 'flexible' | 'compact';
export type TurnstileAppearance = 'always' | 'execute' | 'interaction-only';

export type TurnstileRenderOptions = {
  sitekey: string;
  callback?: (token: string) => void;
  'error-callback'?: (errorCode: string) => void;
  'expired-callback'?: () => void;
  'timeout-callback'?: () => void;
  theme?: TurnstileTheme;
  size?: TurnstileSize;
  appearance?: TurnstileAppearance;
  /**
   * Expliciete taal-code (bv. 'nl', 'en'). Aanrader voor Shadow-DOM-context:
   * Turnstile's auto-detectie walkt anders DOM-parents en kan daar op een
   * ShadowRoot-grens omvallen met "Cannot read properties of undefined
   * (reading 'toLowerCase')".
   */
  language?: string;
};

interface TurnstileGlobal {
  render(container: HTMLElement, options: TurnstileRenderOptions): TurnstileWidgetId;
  reset(widgetId?: TurnstileWidgetId): void;
  remove(widgetId: TurnstileWidgetId): void;
  getResponse(widgetId?: TurnstileWidgetId): string | undefined;
}

declare global {
  interface Window {
    turnstile?: TurnstileGlobal;
  }
}

let scriptPromise: Promise<void> | null = null;

export function ensureTurnstileScript(): Promise<void> {
  if (scriptPromise) return scriptPromise;
  if (typeof document === 'undefined') {
    return Promise.reject(new Error('Turnstile: no document'));
  }

  scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (existing && window.turnstile) {
      resolve();
      return;
    }
    if (existing) {
      // Script tag bestaat al maar global is er nog niet → wacht op load
      existing.addEventListener('load', () => waitForGlobal().then(resolve, reject), { once: true });
      existing.addEventListener('error', () => reject(new Error('Turnstile script failed to load')), { once: true });
      return;
    }
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', () => waitForGlobal().then(resolve, reject), { once: true });
    script.addEventListener('error', () => reject(new Error('Turnstile script failed to load')), { once: true });
    document.head.appendChild(script);
  });

  return scriptPromise;
}

async function waitForGlobal(): Promise<void> {
  // Cloudflare's onload firet net voordat window.turnstile gedefinieerd is —
  // korte poll om de race te overbruggen.
  for (let i = 0; i < 50; i += 1) {
    if (window.turnstile) return;
    await new Promise((r) => setTimeout(r, 100));
  }
  throw new Error('Turnstile: window.turnstile never appeared after script load');
}

/**
 * Rendert een Turnstile-widget in `container` en resolved zodra de widget
 * is geplaatst. De `callback` in options fires asynchroon zodra de user
 * door de challenge is.
 */
export async function renderTurnstileWidget(
  container: HTMLElement,
  options: TurnstileRenderOptions,
): Promise<TurnstileWidgetId> {
  await ensureTurnstileScript();
  if (!window.turnstile) {
    throw new Error('Turnstile not available');
  }
  return window.turnstile.render(container, options);
}

export function resetTurnstileWidget(widgetId: TurnstileWidgetId): void {
  window.turnstile?.reset(widgetId);
}

export function removeTurnstileWidget(widgetId: TurnstileWidgetId): void {
  window.turnstile?.remove(widgetId);
}
